/**
 * Landing screen. Copy is shipping copy from the design handoff — not
 * paraphrased.
 */
import { useNavigate } from 'react-router-dom';
import { Button, Logo, Mono, Wordmark } from '../components/ui';
import { COHORT_SIZE, DELIVERABLES } from './landing-content';
import {
  ClosingCta,
  DeliverableArt,
  Faq,
  Footer,
  HowItWorks,
  ProofStrip,
  SampleReport,
  TrustStrip,
} from './LandingSections';

export function Landing() {
  const navigate = useNavigate();

  return (
    <div style={{ animation: 'ibFadeIn .3s ease both' }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: 'rgba(251,250,248,.86)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid var(--line2)',
        }}
      >
        <div
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            padding: '14px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              marginRight: 'auto',
            }}
          >
            <Logo />
            <Wordmark />
            <span
              style={{
                font: "500 10.5px/1 var(--font-mono)",
                color: 'var(--muted)',
                border: '1px solid var(--line)',
                borderRadius: 20,
                padding: '4px 8px 3px',
              }}
            >
              FREE
            </span>
          </div>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            {/* In-page anchors, hidden on narrow screens where the header
                would otherwise wrap onto two rows. */}
            <div className="ib-nav-links" style={{ display: 'flex', gap: 20 }}>
              {(
                [
                  ['How it works', 'how'],
                  ['Sample', 'sample'],
                  ['FAQ', 'faq'],
                ] as const
              ).map(([label, id]) => (
                <button
                  key={id}
                  onClick={() =>
                    document
                      .getElementById(id)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontSize: 14,
                    fontFamily: 'inherit',
                    color: 'var(--ink2)',
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/auth?mode=in')}>
              Sign in
            </Button>
            {/* Straight into the interview — it runs anonymously, and asking
                for an account first is the drop-off the whole flow avoids. */}
            <Button size="sm" onClick={() => navigate('/capture')}>
              Evaluate my idea
            </Button>
          </nav>
        </div>
      </header>

      <section
        style={{
          maxWidth: 1080,
          margin: '0 auto',
          padding: '76px 24px 64px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--acc-soft)',
            border: '1px solid var(--acc-line)',
            borderRadius: 22,
            padding: '6px 13px 5px',
            marginBottom: 26,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: 'var(--acc)',
            }}
          />
          <Mono size={11.5} color="var(--acc-ink)" style={{ letterSpacing: '.02em' }}>
            AI DEEP RESEARCH · FREE
          </Mono>
        </div>

        <h1
          style={{
            margin: 0,
            fontSize: 'clamp(38px, 7vw, 60px)',
            lineHeight: 1.02,
            letterSpacing: '-.035em',
            fontWeight: 600,
            maxWidth: '16ch',
          }}
        >
          Find out where your idea really stands.
        </h1>
        <p
          style={{
            margin: '24px 0 0',
            fontSize: 19,
            lineHeight: 1.55,
            color: 'var(--muted)',
            maxWidth: '46ch',
          }}
        >
          Six minutes of sharp questions. An instant scan against {COHORT_SIZE}{' '}
          real companies whose outcomes we know. A deep research report in your
          inbox within 48 hours.
        </p>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            justifyContent: 'center',
            marginTop: 34,
          }}
        >
          <Button size="lg" onClick={() => navigate('/capture')}>
            Evaluate my idea
          </Button>
          {/* Smooth-scrolls rather than navigating: the sample is the proof
              that earns the six minutes, and sending people away loses them. */}
          <Button
            size="lg"
            variant="secondary"
            onClick={() =>
              document
                .getElementById('sample')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          >
            See a sample report
          </Button>
        </div>
        <p style={{ margin: '20px 0 0', fontSize: 13.5, color: 'var(--muted)' }}>
          No account to start — sign in only when you&apos;re ready to submit.
        </p>
      </section>

      <section style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px 76px' }}>
        <div
          style={{
            display: 'grid',
            gap: 16,
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          }}
        >
          {DELIVERABLES.map((d) => (
            <div
              key={d.title}
              style={{
                background: d.accent ? 'var(--acc-soft)' : 'var(--card)',
                border: `1px solid ${d.accent ? 'var(--acc-line)' : 'var(--line)'}`,
                borderRadius: 14,
                padding: 22,
              }}
            >
              <div style={{ marginBottom: 14 }}>
                <Mono color={d.accent ? 'var(--acc-ink)' : 'var(--muted)'}>
                  {d.eyebrow}
                </Mono>
              </div>
              <DeliverableArt kind={d.art} />
              <h3
                style={{
                  margin: '0 0 6px',
                  fontSize: 17,
                  fontWeight: 600,
                  letterSpacing: '-.015em',
                }}
              >
                {d.title}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: d.accent ? 'var(--ink2)' : 'var(--muted)',
                }}
              >
                {d.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <HowItWorks />
      <ProofStrip />
      <SampleReport />
      <TrustStrip />
      <Faq />
      <ClosingCta onStart={() => navigate('/capture')} />
      <Footer />
    </div>
  );
}
